import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Tag, Plus, Edit2, Trash2, X, CheckCircle2, Search, Lock } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { XeroAccountPicker } from "@/features/ap/CodingCard";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, Button, Field, Input, Select, Textarea, Spinner, EmptyState } from "@/components/ui";

// =============================================================================
// AdminBrandsScreen — /finance/brands  (T-296)
// Manage supplier brand-collapse rules: brand_map (legal entity → brand) +
// coding_defaults (per-brand suggested Xero account / budget code / FTC / tax).
// Manager+ (matches brand_map RLS app.is_manager_or_above). Used by
// app.collapse_supplier() at AP capture to tag ap_invoices.brand_id and surface
// a coding suggestion. NOTE: suggested_budget_code is DISPLAY-ONLY until the
// budget-code registry is unified (T-310) — it uses the legacy budget_codes
// taxonomy, not the PX/OX/CX finance_budget_codes the AP capture dropdown uses.
// =============================================================================

const FTC_CLASSES = ["", "FTC_OFFROAD_Plant", "FTC_HV_OnRoad", "FTC0_LightVehicle_OnRoad", "FTC_TANK_MIXED"];
// T-503 — must match the values ap_invoices.xero_tax_type accepts, or a brand
// default flows into AP capture and violates the check constraint on save.
// "GST on Capital" / "GST Free Capital" were offered here and are NOT valid
// there; capital purchases carry ordinary GST on Expenses and are separated by
// the ACCOUNT (a Fixed Asset code), not by the tax rate.
const TAX_RATES = ["GST on Expenses", "GST Free Expenses", "BAS Excluded", "GST on Income", "GST Free Income"];

interface BrandRow {
  brand_id?: string;
  brand_display_name: string;
  brand_xero_contact_id: string | null;
  legal_name_patterns: string[];
  abn_list: string[];
  priority: number;
  is_foreign_supplier: boolean;
  notes: string | null;
  coding?: CodingRow | null;
}
interface CodingRow {
  default_id?: string;
  brand_id?: string;
  suggested_xero_account: string;
  suggested_budget_code: string | null;
  suggested_ftc_class: string | null;
  suggested_tax_rate: string;
}

const toArr = (s: string) => (s || "").split(/[\n,]/).map(x => x.trim()).filter(Boolean);

export default function AdminBrandsScreen() {
  const user = useRequireAuth();
  const qc = useQueryClient();
  const canEdit = hasRole(user, "manager");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Partial<BrandRow> | null>(null);

  const { data: brands = [], isLoading } = useQuery({
    queryKey: ["brand_map_admin"],
    enabled: canEdit,
    queryFn: async () => {
      const [{ data: bm, error: e1 }, { data: cd, error: e2 }] = await Promise.all([
        supabase.from("brand_map").select("*").order("priority").order("brand_display_name"),
        supabase.from("coding_defaults").select("*"),
      ]);
      if (e1) throw e1;
      if (e2) throw e2;
      const codingByBrand = new Map((cd ?? []).map((c: any) => [c.brand_id, c]));
      return (bm ?? []).map((b: any) => ({ ...b, coding: codingByBrand.get(b.brand_id) ?? null })) as BrandRow[];
    },
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return brands;
    return brands.filter(b =>
      b.brand_display_name.toLowerCase().includes(q)
      || (b.legal_name_patterns || []).some(p => p.toLowerCase().includes(q))
      || (b.abn_list || []).some(a => a.includes(q))
    );
  }, [brands, search]);

  const saveMut = useMutation({
    mutationFn: async (d: any) => {
      const brandPayload = {
        brand_display_name: (d.brand_display_name || "").trim(),
        brand_xero_contact_id: (d.brand_xero_contact_id || "").trim() || null,
        legal_name_patterns: toArr(d.legal_name_patterns_text).map((s: string) => s.toLowerCase()),
        abn_list: toArr(d.abn_list_text).map((s: string) => s.replace(/\D/g, "")).filter(Boolean),
        priority: Number(d.priority) || 100,
        is_foreign_supplier: !!d.is_foreign_supplier,
        notes: (d.notes || "").trim() || null,
      };
      let brandId = d.brand_id;
      if (brandId) {
        const { error } = await supabase.from("brand_map").update(brandPayload).eq("brand_id", brandId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from("brand_map").insert(brandPayload).select("brand_id").single();
        if (error) throw error;
        brandId = data.brand_id;
      }
      const codingPayload = {
        brand_id: brandId,
        suggested_xero_account: (d.suggested_xero_account || "").trim(),
        suggested_budget_code: (d.suggested_budget_code || "").trim() || null,
        suggested_ftc_class: d.suggested_ftc_class || null,
        suggested_tax_rate: d.suggested_tax_rate || "GST on Expenses",
      };
      const { error: cErr } = await supabase.from("coding_defaults").upsert(codingPayload, { onConflict: "brand_id" });
      if (cErr) throw cErr;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["brand_map_admin"] });
      setEditing(null);
    },
    onError: (e: any) => alert(`Save failed: ${e?.message ?? String(e)}`),
  });

  const deleteMut = useMutation({
    mutationFn: async (brandId: string) => {
      const { error } = await supabase.from("brand_map").delete().eq("brand_id", brandId);
      if (error) throw error; // coding_defaults cascades
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["brand_map_admin"] }),
    onError: (e: any) => alert(`Delete failed: ${e?.message ?? String(e)}`),
  });

  if (!canEdit) {
    return (
      <div className="max-w-md mx-auto p-6 mt-12">
        <Card className="text-center p-6">
          <Lock size={32} className="text-text-muted mx-auto mb-2" />
          <div className="text-[15px] font-semibold mb-1">Manager access only</div>
          <div className="text-[12px] text-text-muted">Brand mapping is restricted to managers and directors.</div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto pb-24">
      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[20px] font-semibold tracking-tight flex items-center gap-2">
            <Tag size={20} className="text-accent" /> Brand mapping
          </h1>
          <div className="text-text-muted text-[12px] mt-1 max-w-2xl">
            Collapse supplier legal entities into one brand (e.g. "BP Gumly Gumly" → BP) so AP receipts
            tag a consistent brand + suggested coding. Matched at capture by ABN, then legal-name pattern.
          </div>
        </div>
        <Button icon={Plus} onClick={() => setEditing({ priority: 100, is_foreign_supplier: false, suggested_tax_rate: "GST on Expenses" } as any)}>
          New brand
        </Button>
      </div>

      <div className="flex items-center gap-2 px-3 py-2 bg-ink-raised border border-line rounded-md w-80 max-w-[70vw] mb-4">
        <Search size={14} className="text-text-muted" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search brand, pattern, ABN…"
          className="bg-transparent outline-none text-text text-[13px] flex-1" />
      </div>

      {isLoading ? (
        <div className="py-16 flex justify-center"><Spinner /></div>
      ) : filtered.length === 0 ? (
        <Card><EmptyState icon={Tag} title="No brands" description="Add your first brand-collapse rule to get started." /></Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-line">
                  {["Brand", "Match patterns", "ABNs", "Priority", "Suggested coding", ""].map(h => (
                    <th key={h} className="text-left px-4 py-2 text-[10px] font-medium uppercase tracking-wider2 text-text-muted">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((b, idx) => (
                  <tr key={b.brand_id} className={`border-b border-line last:border-b-0 ${idx % 2 ? "bg-ink/40" : ""}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-text flex items-center gap-2">
                        {b.brand_display_name}
                        {b.is_foreign_supplier && <Pill tone="bid">foreign</Pill>}
                      </div>
                      {b.brand_xero_contact_id && <div className="text-[10px] font-mono text-text-dim mt-0.5">Xero: {b.brand_xero_contact_id.slice(0, 12)}…</div>}
                    </td>
                    <td className="px-4 py-2.5 text-[12px] text-text-muted max-w-[260px]">
                      {(b.legal_name_patterns || []).join(", ") || "—"}
                    </td>
                    <td className="px-4 py-2.5 text-[12px] font-mono text-text-muted">{(b.abn_list || []).join(", ") || "—"}</td>
                    <td className="px-4 py-2.5 font-mono text-text-muted">{b.priority}</td>
                    <td className="px-4 py-2.5 text-[12px]">
                      {b.coding ? (
                        <div className="text-text-muted">
                          <span className="text-text">{b.coding.suggested_xero_account}</span>
                          <span className="text-text-dim"> · {b.coding.suggested_tax_rate}</span>
                          {b.coding.suggested_budget_code && <span className="text-text-dim"> · {b.coding.suggested_budget_code}</span>}
                          {b.coding.suggested_ftc_class && <span className="text-text-dim"> · FTC</span>}
                        </div>
                      ) : <span className="text-warn text-[11px]">no coding set</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <button onClick={() => setEditing({
                        ...b,
                        legal_name_patterns_text: (b.legal_name_patterns || []).join(", "),
                        abn_list_text: (b.abn_list || []).join(", "),
                        suggested_xero_account: b.coding?.suggested_xero_account ?? "",
                        suggested_budget_code: b.coding?.suggested_budget_code ?? "",
                        suggested_ftc_class: b.coding?.suggested_ftc_class ?? "",
                        suggested_tax_rate: b.coding?.suggested_tax_rate ?? "GST on Expenses",
                      } as any)} className="p-1.5 text-text-muted hover:text-text"><Edit2 size={13} /></button>
                      <button onClick={() => { if (confirm(`Delete brand "${b.brand_display_name}"? This also removes its coding defaults.`)) deleteMut.mutate(b.brand_id!); }}
                        className="p-1.5 text-text-muted hover:text-danger"><Trash2 size={13} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {editing && (
        <BrandEditor d={editing} onClose={() => setEditing(null)} onSave={saveMut.mutate} saving={saveMut.isPending} />
      )}
    </div>
  );
}

function BrandEditor({ d, onClose, onSave, saving }: any) {
  const [s, setS] = useState<any>(d);
  const set = (k: string, v: any) => setS({ ...s, [k]: v });

  // T-503 — the live chart of accounts, same source the AP coding card uses, so
  // a brand default and an invoice coding can never offer different lists.
  const { data: xeroAccounts = [] } = useQuery({
    queryKey: ["xero_accounts"],
    queryFn: async () => {
      const { data } = await supabase.from("xero_accounts")
        .select("code, name, account_type, tax_code, description")
        .eq("active", true).eq("usable_for_ap", true).order("code");
      return (data ?? []) as any[];
    },
  });
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center md:justify-center p-0 md:p-6" onClick={onClose}>
      <div className="bg-ink-card border border-line w-full md:max-w-lg md:rounded-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-line flex items-center justify-between">
          <h3 className="text-lg font-semibold">{d.brand_id ? "Edit" : "New"} brand</h3>
          <button onClick={onClose} className="text-text-muted hover:text-text p-1"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Brand display name *">
            <Input value={s.brand_display_name ?? ""} onChange={e => set("brand_display_name", e.target.value)} placeholder="e.g. BP" />
          </Field>
          <Field label="Legal-name match patterns" hint="Comma or line separated. Lowercased substrings matched against a supplier's legal name (e.g. 'bp ', 'gumly').">
            <Textarea rows={2} value={s.legal_name_patterns_text ?? ""} onChange={e => set("legal_name_patterns_text", e.target.value)} placeholder="bp, gumly gumly" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="ABNs" hint="Digits only; ABN match wins.">
              <Input value={s.abn_list_text ?? ""} onChange={e => set("abn_list_text", e.target.value)} placeholder="51004085616" />
            </Field>
            <Field label="Priority" hint="Lower = checked first.">
              <Input type="number" mono value={s.priority ?? 100} onChange={e => set("priority", e.target.value)} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Foreign supplier?">
              <Select value={s.is_foreign_supplier ? "yes" : "no"} onChange={e => set("is_foreign_supplier", e.target.value === "yes")}>
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </Select>
            </Field>
            <Field label="Xero contact ID" hint="Optional; set after first push.">
              <Input value={s.brand_xero_contact_id ?? ""} onChange={e => set("brand_xero_contact_id", e.target.value)} />
            </Field>
          </div>

          <div className="pt-2 border-t border-line">
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono mb-3">Suggested coding (at capture)</div>
            {/* T-503 — the same searchable picker the AP coding card uses, fed by
                the live chart of accounts. This was a free-text box, which is how
                "323 - Materials & Site Consumables" ended up stored as a code and
                got a document rejected by Xero. A brand default that cannot be
                typed cannot be typed wrong. */}
            <Field label="Xero account *" hint="Search by code, name or what it's for — from the live Xero chart of accounts">
              <XeroAccountPicker
                value={String(s.suggested_xero_account ?? "").trim()}
                accounts={xeroAccounts}
                onChange={(code) => set("suggested_xero_account", code)}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Field label="Tax rate *">
                <Select value={s.suggested_tax_rate ?? "GST on Expenses"} onChange={e => set("suggested_tax_rate", e.target.value)}>
                  {TAX_RATES.map(t => <option key={t} value={t}>{t}</option>)}
                </Select>
              </Field>
              <Field label="FTC class">
                <Select value={s.suggested_ftc_class ?? ""} onChange={e => set("suggested_ftc_class", e.target.value)}>
                  {FTC_CLASSES.map(f => <option key={f} value={f}>{f || "— none —"}</option>)}
                </Select>
              </Field>
            </div>
            <Field label="Budget code (display-only until T-310)" hint="Legacy budget_codes taxonomy — shown as a suggestion, not auto-applied yet.">
              <Input value={s.suggested_budget_code ?? ""} onChange={e => set("suggested_budget_code", e.target.value)} placeholder="5022-FUEL-LV" />
            </Field>
          </div>

          <Field label="Notes"><Textarea rows={2} value={s.notes ?? ""} onChange={e => set("notes", e.target.value)} /></Field>
        </div>
        <div className="p-4 border-t border-line bg-ink-raised flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button icon={CheckCircle2} disabled={!s.brand_display_name || !s.suggested_xero_account || saving} onClick={() => onSave(s)}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
